import {FC, SyntheticEvent, useCallback, useState, useEffect, Fragment} from "react";
import styled from "@emotion/styled";
import {LOCALE} from "modules/constants";

import {Exist, PrimaryButtonDark} from "components";
import cflLogoFr from "assets/images/team-quiz-fr-logo.png";
import cflLogo from "assets/images/team-quiz-logo.png";
import {Checkbox} from "components/Form/CheckboxMui";
import {useStore} from "modules/hooks";
import {BaseModal} from "components/Modal/BaseModal";
import {RequestState, Language} from "modules/enums";
import {FormControl} from "@mui/material";
import {observer} from "mobx-react";

const Wrapper = styled.div`
	.registration {
		background: rgb(84, 84, 86);
	}
`;

export const FormContainer = styled.div`
	display: flex;
	align-items: center;
	flex-direction: column;
	max-width: 320px;
	margin: 0 auto;
`;

export const FormField = styled(FormControl)`
	&.MuiFormControl-root {
		width: 100%;
		margin-bottom: 16px;
	}
`;

export const FormError = styled.div`
	color: #fff;
`;

const Img = styled.img`
	width: 125px;
	margin-bottom: 24px;
`;

export const Title = styled.h2`
	text-align: center;
	color: #fff;
	font-size: 20px;
	font-weight: 700;
	margin-bottom: 20px;
`;

const Link = styled.a`
	font-weight: bold;
	text-decoration: underline;
`;

export const SSOAdditionalFieldsModal: FC = observer(() => {
	const {profile, user} = useStore();

	const isOpen = profile.apiState === RequestState.Received && !profile.terms;

	useEffect(() => {
		if (user.id) {
			void profile.request();
		}
	}, [profile, user.id]);

	if (!isOpen) {
		return null;
	}

	return <SSOAdditionalFieldsForm />;
});

const LOCALE_DATA = {
	[Language.English]: {
		logo: cflLogo,
		termsLink:
			"https://www.cfl.ca/2024-cfl-team-picker-presented-by-world-vision-contest-rules-and-regulations/",
	},
	[Language.French]: {
		logo: cflLogoFr,
		termsLink:
			"https://www.cfl.ca/concours-selectionneur-dequipe-de-la-lcf-2024-presente-par-vision-mondiale/",
	},
};
export const SSOAdditionalFieldsForm: FC = () => {
	const {I18n, user, profile} = useStore();

	const isShowMarketingEmails = !user?.optInGZ;
	const [isValid, setIsValid] = useState(false);
	const isDisabled = !isValid;
	const localeData = LOCALE_DATA[LOCALE];

	const handleSubmit = useCallback(
		(e: SyntheticEvent<HTMLFormElement>) => {
			e.preventDefault();
			const {terms, isEmailsEnabled, sponsorOptIn}: Record<string, HTMLInputElement> =
				e.currentTarget;

			// If the field is not present, notifications have been enabled previously.
			const optInGZ = isEmailsEnabled ? isEmailsEnabled.checked : true;

			void profile.save({
				terms: terms.checked,
				sponsorOptIn: sponsorOptIn.checked,
				marketingEmails: optInGZ,
			});
		},
		[profile]
	);

	const handleOnChange = useCallback(
		(e: SyntheticEvent<HTMLFormElement>) => {
			const {terms}: Record<string, HTMLInputElement> = e.currentTarget;

			const validateList = [terms.checked];

			setIsValid(validateList.every((el) => el));
			profile.clearErrors();
		},
		[setIsValid, profile]
	);

	if (!profile) {
		return null;
	}

	const replaceLinkTranslation = (i18nValue: string, tag: string, link: string) => {
		const regex = new RegExp(`(.+)<${tag}>(.+)<\\/${tag}>(.+)`, "gm");
		const execValue = regex.exec(i18nValue);

		if (execValue) {
			const [value, group1, group2, group3] = execValue;
			const hasGroups = [group1, group2, group3].every(Boolean);

			return hasGroups ? (
				<Fragment>
					{group1}
					<Link href={link} target="_blank" rel="noreferrer">
						{group2}
					</Link>
					{group3}
				</Fragment>
			) : (
				value
			);
		}

		return i18nValue;
	};

	return (
		<Wrapper>
			<BaseModal className={"registration"}>
				<form noValidate onSubmit={handleSubmit} onChange={handleOnChange}>
					<FormContainer>
						<Img src={localeData.logo} alt="All-Star" />
						<Title>{I18n.t("modal.sso.title")}</Title>

						<FormField>
							<Checkbox
								name="terms"
								text={
									<span>
										{replaceLinkTranslation(
											I18n.t("modal.sso.terms"),
											"link1",
											localeData.termsLink
										)}
									</span>
								}
							/>
						</FormField>

						<FormField>
							<Checkbox
								name="isEmailsEnabled"
								text={I18n.t("modal.sso.marketing_emails")}
							/>
						</FormField>

						<Exist when={isShowMarketingEmails}>
							<FormField>
								<Checkbox
									name="sponsorOptIn"
									text={
										<span>
											{replaceLinkTranslation(
												I18n.t("modal.sso.sponsor_opt_in"),
												"link1",
												"https://worldvision.ca/cfl"
											)}
										</span>
									}
								/>
							</FormField>
						</Exist>

						<Exist when={Boolean(profile.error)}>
							<FormField>
								<FormError>{I18n.t(profile.error || "")}</FormError>
							</FormField>
						</Exist>

						<FormField>
							<PrimaryButtonDark disabled={isDisabled} type="submit">
								{I18n.t("modal.sso.button")}
							</PrimaryButtonDark>
						</FormField>
					</FormContainer>
				</form>
			</BaseModal>
		</Wrapper>
	);
};
